import { truncate } from '~/fc/Helpers/StringHelpers'

/**
 * Cleans a string by removing special characters and normalizing whitespace.
 *
 * @param {string} name - The input string to clean.
 * @returns {string} The sanitized and normalized string.
 */
export const getProperName = (name) =>
	name.replace(/[/\\#+$~%'"*?<>{}]/g, '').replace(/\s+/g, ' ');


/**
 * Extracts the file name from a given URL, excluding query parameters and fragments.
 *
 * @param {string} url - The URL to extract the file name from.
 * @returns {string} The extracted file name or an empty string if the URL is invalid.
 */
export const getFileName = (url) => {
	if (!url) return '';
	let name = url.substring(url.lastIndexOf('/') + 1).split('?')[0];
	return name.includes('#') && name.lastIndexOf('#') > name.lastIndexOf('.')
		? name.substring(0, name.lastIndexOf('#'))
		: name;
};

/**
 * Extracts the file extension from a URL.
 * 
 * @param {string} url - The URL to extract the file extension from.
 * @returns {string} - The file extension in lowercase, or an empty string if not found.
 */
export function getExt(url) {
	const ext = getFileName(url).split('.').pop();
	return ext && ext !== url ? ext.toLowerCase() : "";
}

/**
 * Extracts the file extension from the file's signature.
 * 
 * @param {File} file - The file from which to extract the extension.
 * @returns {Promise<string>} - A promise that resolves to the file extension.
 */
export function getExtFromFile(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const ext = getExtensionFromFileSignature(new Uint8Array(e.target.result)
				.slice(0, 4)
				.map(byte => byte.toString(16))
				.join("")
				.toUpperCase());
			resolve(ext);
		};
		reader.onerror = reject;
		reader.readAsArrayBuffer(file.slice(0, 4));
	});
}


/**
 * Generates a pseudo-random UUIDv4 string of 12 characters.
 *
 * @returns {string} A 12-character UUIDv4 string.
 */
export const uuidv4 = () => {
	return "xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}


/**
 * Formats a file size into a human-readable string (B, KB, MB).
 *
 * @param {number|string} size - The file size in bytes.
 * @returns {string} The formatted size string.
 */
export const formatSize = (size) => {
	size = parseFloat(size);
	return size > 1024 * 1024 ? (size / (1024 * 1024)).toFixed(2) + ' MB' :
		size > 1024 ? (size / 1024).toFixed(2) + ' KB' :
			size > 0 ? size.toFixed(2) + ' B' : '';
};


/**
 * Truncates a file's name to 50 characters and returns the updated file object.
 * 
 * @param {Object} file - The file object containing a `name` property.
 * @returns {Object} A new file object with the truncated name.
 */
export const truncateName = file => ({ ...file, name: truncate(file.name, 50) });


/**
 * Retrieves a list of allowed file extensions based on the provided info object.
 * Prioritizes customVariables.allowedExts, then deduplicates and formats allowedArr.
 * 
 * @param {Object} info - Page Info from the system.
 * @returns {string} A comma-separated string of allowed file extensions or an empty string.
 */
export function getAllowedFileList(info) {
	return info?.customVariables?.allowedExts
		|| (info?.limitAcceptedFiles && info.allowedArr?.filter((v, i, self) => self.indexOf(v) === i).join(","))
		|| "";
}

/**
 * Returns the file extension based on its signature.
 * 
 * @param {string} signature - The file signature to check.
 * @returns {string} - The file extension or an empty string if not found.
 */
function getExtensionFromFileSignature(signature) {
	return ["FFFB", "FFFA", "FFF3", "FFF2", "FFE3", "494433"]
		.some(el => signature.startsWith(el)) ? "mp3" : "";
}


/**
 * Extracts the file extension from a URL.
 * 
 * @param {string} url - The URL to extract the extension from.
 * @returns {string|null} - The file extension or null if none found.
 */
export function getUrlExtension(url) {
	const name = new URL(url).pathname.split("/").pop().split("?")[0];
	return name.includes('.') ? name.split('.').pop() : null;
}
